/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import type Store from "@ember-data/store";
import { service } from "@ember/service";
import { underscore } from "@ember/string";
import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import dayjs from "dayjs";
import { task } from "ember-concurrency";
import BioExamResultsReceivedJournalEvent from "parog-web/models/bio-exam-results-received-journal-event";
import ConsultationReportWrittenJournalEvent from "parog-web/models/consultation-report-written-journal-event";
import DmpDocumentRetrievedJournalEvent from "parog-web/models/dmp-document-retrieved-journal-event";
import type FileAttachment from "parog-web/models/file-attachment";
import FileUploadedJournalEvent from "parog-web/models/file-uploaded-journal-event";
import type JournalEvent from "parog-web/models/journal-event";
import extensionFromMimeType from "parog-web/utils/extension-from-mime-type";

interface Signature<T, K> {
  Blocks: {
    default: [
      {
        downloadFileFromFileAttachmentTask: T;
        downloadFileFromContentTask: K;
      }
    ];
  };
}

export default class FileAttachmentDownloader<T, K> extends Component<
  Signature<T, K>
> {
  @service declare store: Store;

  @tracked shouldShowErrorMessage = false;

  /**
   * Given the fileAttachmentId, find from the database
   * the related File attachment, and download it
   */
  downloadFileFromFileAttachmentTask = task(
    async (fileAttachment: FileAttachment) => {
      const fileContent = await fileAttachment.fileContent;
      const journalEvent = await fileAttachment.journalEvent;
      const mimeType = fileAttachment.mimeType;

      if (fileContent && journalEvent && mimeType) {
        const linkSource = `data:${mimeType};base64,${fileContent.data}`;
        const fileName = FileAttachmentDownloader.filename(journalEvent);

        this.processDownload(linkSource, mimeType, fileName);
      } else {
        this.shouldShowErrorMessage = true;
      }
    }
  );

  /**
   * From the linkSource, download the file
   * linkSource has the format data:MIMETYPE;base64,CONTENT
   */
  downloadFileFromContentTask = task(
    async (linkSource: string, mimeType: string, fileName: string) => {
      this.processDownload(linkSource, mimeType, fileName);
    }
  );

  /**
   * Construct a link, populate the mandatory attributes
   * such as :
   * - linkSource, which contains the data
   * - mimeType, to determine the extension
   * - the fileName, i.e. name of the download file
   */
  processDownload(
    linkSource: string,
    mimeType: string,
    fileName: string
  ): void {
    const downloadLink = document.createElement("a");
    downloadLink.href = linkSource;
    downloadLink.download = fileName + extensionFromMimeType(mimeType);
    downloadLink.click();
  }

  static filename(journalEvent: JournalEvent): string {
    const formattedDate = dayjs(journalEvent.occurredAt).format("DD-MM-YYYY");
    let fileName: string;

    if (journalEvent instanceof ConsultationReportWrittenJournalEvent) {
      fileName = `rapport_consultation_${formattedDate}`;
    } else if (journalEvent instanceof BioExamResultsReceivedJournalEvent) {
      fileName = `resultats_biologiques_${formattedDate}`;
    } else if (
      journalEvent instanceof FileUploadedJournalEvent ||
      journalEvent instanceof DmpDocumentRetrievedJournalEvent
    ) {
      fileName = `${underscore(journalEvent.title)}_${formattedDate}`;
    } else {
      const jeName = underscore(
        journalEvent.constructor.name.replace(/JournalEvent/, "")
      );
      fileName = `${underscore(jeName)}_${formattedDate}`;
    }

    return fileName;
  }
}
